import { Link, Outlet, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useState } from "react";
import { PublicConfigProvider, usePublicConfig } from "@/context/PublicConfigContext";

// WHATSAPP se mantiene exportado como FALLBACK para compat con imports existentes.
// Preferir siempre `usePublicConfig().whatsapp` en componentes nuevos.
export const WHATSAPP = "50243036012";

export default function PublicLayout() {
  return (
    <PublicConfigProvider>
      <PublicLayoutInner />
    </PublicConfigProvider>
  );
}

function PublicLayoutInner() {
  const { totalItems } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isActive = (p) => location.pathname === p;
  const cfg = usePublicConfig();
  const whatsapp = (cfg.whatsapp || WHATSAPP).replace(/\D/g, "");
  // Formato bonito: 4 dígitos - 4 dígitos (mostrar sólo últimos 8 quitando 502)
  const displayPhone = (() => {
    const n = whatsapp.startsWith("502") ? whatsapp.slice(3) : whatsapp;
    if (n.length === 8) return `${n.slice(0,4)}-${n.slice(4)}`;
    return n;
  })();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top bar */}
      <div className="bg-black text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 h-9 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline">🚚 Envíos a toda Guatemala</span>
            <span className="sm:hidden">🚚 Envíos GT</span>
          </div>
          <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[#FFD100]" data-testid="topbar-whatsapp">
            <Phone className="w-3 h-3" /> {displayPhone}
          </a>
        </div>
      </div>

      {/* Header amarillo comercial */}
      <header className="sticky top-0 z-40 bg-[#FFB800] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-14 md:h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <span className="text-xl md:text-2xl font-black tracking-tight leading-none">
              <span className="text-black">CASA</span><span className="text-white">COMPLETA</span><span className="text-black text-[10px] md:text-xs align-super font-black ml-0.5">.COM</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-bold text-black">
            <Link to="/" className={`hover:text-[#E30613] ${isActive("/") ? "text-[#E30613]" : ""}`} data-testid="nav-home">INICIO</Link>
            <Link to="/carrito" className={`hover:text-[#E30613] ${isActive("/carrito") ? "text-[#E30613]" : ""}`} data-testid="nav-carrito">CARRITO</Link>
            <a href="#contacto" className="hover:text-[#E30613]">CONTACTO</a>
            <Link to="/login" className="hover:text-[#E30613] opacity-60">Ingresar</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/carrito" className="relative bg-black text-white p-2 md:p-2.5 rounded-full hover:bg-[#E30613] transition-colors" data-testid="cart-icon">
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#E30613] text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-badge">{totalItems}</span>
              )}
            </Link>
            <button className="md:hidden p-2 text-black" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-black/20 bg-[#FFB800] px-4 py-3 space-y-1 text-sm font-bold text-black">
            <Link to="/" onClick={() => setMenuOpen(false)} className="block py-2">INICIO</Link>
            <Link to="/carrito" onClick={() => setMenuOpen(false)} className="block py-2">CARRITO</Link>
            <a href="#contacto" onClick={() => setMenuOpen(false)} className="block py-2">CONTACTO</a>
            <Link to="/login" onClick={() => setMenuOpen(false)} className="block py-2 opacity-60">Ingresar</Link>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer id="contacto" className="bg-black text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="text-2xl font-black tracking-tight text-[#FFD100]">
              CASA<span className="text-[#E30613]">COMPLETA</span>
            </div>
            <p className="text-sm text-zinc-400 mt-3">Todo para tu hogar al mejor precio. Envíos a toda Guatemala.</p>
          </div>
          <div>
            <h4 className="font-bold text-[#FFD100] mb-3">CONTACTO</h4>
            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#FFD100] mb-2">
              <Phone className="w-4 h-4" /> WhatsApp: {displayPhone}
            </a>
            <div className="flex items-center gap-2 text-sm text-zinc-300">
              <MapPin className="w-4 h-4" /> Guatemala
            </div>
          </div>
          <div>
            <h4 className="font-bold text-[#FFD100] mb-3">SÍGUENOS</h4>
            <div className="flex gap-3">
              <a href="#" className="bg-zinc-800 p-2 rounded-full hover:bg-[#E30613]"><Instagram className="w-4 h-4" /></a>
              <a href="#" className="bg-zinc-800 p-2 rounded-full hover:bg-[#E30613]"><Facebook className="w-4 h-4" /></a>
            </div>
          </div>
          <div>
            <h4 className="font-bold text-[#FFD100] mb-3">FORMAS DE PAGO</h4>
            <p className="text-sm text-zinc-400">Efectivo · Transferencia · Depósito bancario</p>
            <p className="text-xs text-zinc-500 mt-2">Todos los pedidos se coordinan por WhatsApp</p>
          </div>
        </div>
        <div className="border-t border-zinc-800 py-4 text-center text-xs text-zinc-500">
          © {new Date().getFullYear()} CASACOMPLETA.COM · Todos los derechos reservados
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer"
         className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-2xl flex items-center gap-2 transition-transform hover:scale-110"
         data-testid="floating-whatsapp">
        <Phone className="w-6 h-6" />
      </a>
    </div>
  );
}
